import type { ReactNode } from 'react'

// A deliberately small markdown renderer for the About tab's profile prose: h2/h3 headings,
// paragraphs, unordered lists, a `---` rule, and inline **bold**, *italic*/_italic_ and
// [links](url). The content is station-owner-authored and lives in the repo — not user input —
// so we render to React ELEMENTS (never dangerouslySetInnerHTML) and simply pass through any
// syntax we don't support rather than pull in a full markdown parser. Links open in a new tab.
//
// Inline parsing RECURSES into each match's content, so emphasis and links nest correctly — a
// link wrapped in bold (**[Wikipedia](…)**, common in a bulleted list) renders as a real bold
// link rather than the literal bracketed text an earlier single-pass version left behind.
const INLINE = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_/

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  let rest = text
  let i = 0
  while (rest.length) {
    const m = INLINE.exec(rest)
    if (!m) {
      out.push(rest)
      break
    }
    if (m.index > 0) out.push(rest.slice(0, m.index))
    const k = `${key}-${i++}`
    if (m[1] != null) {
      out.push(
        <a key={k} href={m[2]} target="_blank" rel="noopener noreferrer">
          {inline(m[1], k)}
        </a>,
      )
    } else if (m[3] != null) {
      out.push(<strong key={k}>{inline(m[3], k)}</strong>)
    } else {
      out.push(<em key={k}>{inline(m[4] ?? m[5], k)}</em>)
    }
    rest = rest.slice(m.index + m[0].length)
  }
  return out
}

export function Markdown({ source }: { source: string }) {
  const blocks: ReactNode[] = []
  let para: string[] = []
  let list: string[] = []
  let b = 0

  const flushPara = () => {
    if (para.length) blocks.push(<p key={`p${b++}`}>{inline(para.join(' '), `p${b}`)}</p>)
    para = []
  }
  const flushList = () => {
    if (list.length) {
      const items = list
      blocks.push(
        <ul key={`u${b++}`}>
          {items.map((li, n) => (
            <li key={n}>{inline(li, `u${b}-${n}`)}</li>
          ))}
        </ul>,
      )
    }
    list = []
  }
  const flush = () => {
    flushPara()
    flushList()
  }

  for (const raw of source.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim()
    if (!line) {
      flush()
    } else if (line.startsWith('### ')) {
      flush()
      blocks.push(<h3 key={`h${b++}`}>{inline(line.slice(4), `h${b}`)}</h3>)
    } else if (line.startsWith('## ')) {
      flush()
      blocks.push(<h2 key={`h${b++}`}>{inline(line.slice(3), `h${b}`)}</h2>)
    } else if (line === '---') {
      flush()
      blocks.push(<hr key={`hr${b++}`} />)
    } else if (line.startsWith('- ')) {
      flushPara()
      list.push(line.slice(2))
    } else {
      flushList()
      para.push(line)
    }
  }
  flush()

  return <>{blocks}</>
}
